import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "../Header";
import Footer from "../Footer";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { 
  MapPinIcon,
  StarIcon,
  ClockIcon,
  ShieldCheckIcon,
  HeartHandshakeIcon,
  SearchIcon,
  PhoneIcon,
  NavigationIcon,
  BedIcon,
  UserCheckIcon,
  ActivityIcon
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { clinicService } from "../../services";
import Breadcrumb from "../Breadcrumb";
import { openWhatsApp } from "../../lib/utils";

const OurHospitals = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCity, setSelectedCity] = useState("all-cities");
  const [hospitals, setHospitals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch hospitals from API
  useEffect(() => {
    const fetchHospitals = async () => {
      try {
        setLoading(true);
        const hospitalsData = await clinicService.getAllClinics();
        
        // Extract data from processed API response
        let hospitalsArray = [];
        if (Array.isArray(hospitalsData?.data)) {
          hospitalsArray = hospitalsData.data;                  // processed by handleApiRequest
        } else if (Array.isArray(hospitalsData?.result?.docs)) {
          hospitalsArray = hospitalsData.result.docs;            // raw API fallback
        } else if (Array.isArray(hospitalsData?.docs)) {
          hospitalsArray = hospitalsData.docs;                   // older shape fallback
        } else if (Array.isArray(hospitalsData)) {
          hospitalsArray = hospitalsData;                       // final fallback
        }
        
        setHospitals(hospitalsArray);
      } catch (err) {
        console.error('Error fetching hospitals:', err);
        // Show no data on error
        setError(null);
        setHospitals([]);
      } finally {
        setLoading(false);
      }
    };
    fetchHospitals();
  }, []);

  const filteredHospitals = Array.isArray(hospitals) ? hospitals.filter(hospital => {
    return (
      (searchTerm === "" || 
       hospital.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
       hospital.city?.toLowerCase().includes(searchTerm.toLowerCase())) &&
      (selectedCity === "all-cities" || selectedCity === "" || hospital.city === selectedCity)
    );
  }) : [];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading hospitals...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <Button onClick={() => window.location.reload()}>Try Again</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-cyan-50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumb 
            items={[
              { label: "Our Hospitals" }
            ]} 
          />
          <div className="text-center mb-6 sm:mb-8">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
              Our <span className="text-blue-600">Hospitals</span>
            </h1>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto px-4 sm:px-0">
              State-of-the-art medical facilities with world-class infrastructure and experienced healthcare professionals
            </p>
          </div>

          {/* Search Filters */}
          <Card className="max-w-3xl mx-auto">
            <CardContent className="p-4 sm:p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                <div className="relative">
                  <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <Input
                    placeholder="Search hospitals, city..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                
                <Select value={selectedCity} onValueChange={setSelectedCity}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select City" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all-cities">All Cities</SelectItem>
                    <SelectItem value="Faridabad">Faridabad</SelectItem>
                    <SelectItem value="Delhi">Delhi</SelectItem>
                    <SelectItem value="Gurgaon">Gurgaon</SelectItem>
                    <SelectItem value="Noida">Noida</SelectItem>
                  </SelectContent>
                </Select>

                <Button className="bg-blue-600 hover:bg-blue-700 sm:col-span-2 lg:col-span-1">
                  <SearchIcon className="w-4 h-4 mr-2" />
                  Search
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Hospitals List */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900">
              {filteredHospitals.length} Hospitals Available
            </h2>
            <p className="text-gray-600">Find the nearest hospital for your medical needs</p>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 sm:gap-8">
            {filteredHospitals.length > 0 ? filteredHospitals.map((hospital) => (
              <Card key={hospital.id || hospital._id} className="hover:shadow-xl transition-shadow">
                <CardContent className="p-0">
                  {/* Hospital Image */}
                  {hospital.imageUrl ? (
                    <div className="h-40 sm:h-48 relative overflow-hidden">
                      <img
                        src={hospital.imageUrl}
                        alt={hospital.name || 'Hospital'}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                      <div className="absolute bottom-4 left-4 right-4">
                        <div className="text-sm font-semibold bg-blue-600 bg-opacity-90 px-4 py-1 rounded-full text-white inline-block">
                          {hospital.type || 'Super Specialty Hospital'}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="h-40 sm:h-48 bg-gradient-to-br from-blue-100 to-cyan-100 relative overflow-hidden">
                      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-cyan-600/20"></div>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="text-center text-white">
                          <div className="w-20 h-20 bg-white bg-opacity-20 rounded-2xl flex items-center justify-center mb-4 mx-auto">
                            <HeartHandshakeIcon className="w-10 h-10" />
                          </div>
                          <div className="text-sm font-semibold bg-blue-600 bg-opacity-90 px-4 py-1 rounded-full">
                            {hospital.type || 'Super Specialty Hospital'}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="p-4 sm:p-6">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-3 sm:mb-4">
                      <div>
                        <h3 className="text-lg sm:text-xl font-bold text-gray-900">{hospital.name || 'Hospital Name'}</h3>
                        <div className="flex items-center space-x-1 mt-1">
                          <StarIcon className="w-4 h-4 fill-green-500 text-green-500" />
                          <span className="text-sm font-semibold text-green-600">
                            {hospital.rating || 'N/A'}/5
                          </span>
                          <span className="text-xs text-gray-500">
                            ({hospital.reviews || 0} reviews)
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Address */}
                    <div className="flex items-start space-x-2 mb-4">
                      <MapPinIcon className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-gray-600 leading-relaxed">
                        {hospital.address || 'Address not available'}
                      </p>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-3 sm:mb-4">
                      <div className="text-center">
                        <div className="flex items-center justify-center mb-1">
                          <BedIcon className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600" />
                        </div>
                        <div className="text-base sm:text-lg font-bold text-gray-900">{hospital.beds || 'N/A'}</div>
                        <div className="text-xs text-gray-500">Beds</div>
                      </div>
                      <div className="text-center">
                        <div className="flex items-center justify-center mb-1">
                          <UserCheckIcon className="w-3 h-3 sm:w-4 sm:h-4 text-green-600" />
                        </div>
                        <div className="text-base sm:text-lg font-bold text-gray-900">{hospital.doctors || 'N/A'}</div>
                        <div className="text-xs text-gray-500">Doctors</div>
                      </div>
                      <div className="text-center">
                        <div className="flex items-center justify-center mb-1">
                          <ActivityIcon className="w-3 h-3 sm:w-4 sm:h-4 text-purple-600" />
                        </div>
                        <div className="text-base sm:text-lg font-bold text-gray-900">{hospital.specialties?.length || 0}</div>
                        <div className="text-xs text-gray-500">Specialties</div>
                      </div>
                    </div>

                    {/* Features */}
                    {hospital.features && hospital.features.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {hospital.features.map((feature, idx) => (
                          <Badge key={`${feature}-${idx}`} variant="secondary" className="text-xs">
                            {feature}
                          </Badge>
                        ))}
                      </div>
                    )}

                    {/* Specialties */}
                    {hospital.specialties && hospital.specialties.length > 0 && (
                      <div className="mb-4">
                        <h4 className="text-sm font-semibold text-gray-900 mb-2">Key Specialties:</h4>
                        <div className="flex flex-wrap gap-1">
                          {hospital.specialties.slice(0, 3).map((specialty, idx) => (
                            <span key={`${specialty}-${idx}`} className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded">
                              {specialty}
                            </span>
                          ))}
                          {hospital.specialties.length > 3 && (
                            <span className="text-xs text-gray-500">
                              +{hospital.specialties.length - 3} more
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex flex-col xs:flex-row gap-2 sm:gap-3">
                      <Link to={`/hospital/${hospital.id || hospital._id}`} className="flex-1">
                        <Button variant="outline" className="w-full text-xs sm:text-sm" size="sm">
                          View Details
                        </Button>
                      </Link>
                      <Button className="flex-1 bg-gray-900 hover:bg-gray-600 text-xs sm:text-sm" size="sm" onClick={() => openWhatsApp("9311720045", "Hi, I am contacting you for treatment from TheLivCure site.")}>
                        <PhoneIcon className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                        <span className="hidden xs:inline">Call Hospital</span>
                        <span className="xs:hidden">Call</span>
                      </Button>
                      <Button className="flex-1 bg-blue-600 hover:bg-blue-700 text-xs sm:text-sm" size="sm">
                        <NavigationIcon className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                        <span className="hidden xs:inline">Get Directions</span>
                        <span className="xs:hidden">Directions</span>
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )) : (
              <div className="col-span-full text-center py-12">
                <HeartHandshakeIcon className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">No Hospitals Found</h3>
                <p className="text-gray-500 mb-6">
                  {searchTerm || selectedCity !== "all-cities" 
                    ? "Try adjusting your search filters" 
                    : "No hospitals available at the moment"}
                </p>
                {(searchTerm || selectedCity !== "all-cities") && (
                  <Button 
                    onClick={() => {
                      setSearchTerm("");
                      setSelectedCity("all-cities");
                    }}
                    variant="outline"
                  >
                    Clear Filters
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Network Stats temporarily disabled */}
      {false && (
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl p-8 lg:p-12 text-center text-white">
            <h2 className="text-3xl font-bold mb-4">India's Largest Surgery Network</h2>
            <p className="text-lg opacity-90 mb-8 max-w-3xl mx-auto">Trust in our extensive network of hospitals and clinics across India</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              <div><div className="text-3xl font-bold mb-2">150+</div><div className="text-sm opacity-80">Hospitals</div></div>
              <div><div className="text-3xl font-bold mb-2">30+</div><div className="text-sm opacity-80">Cities</div></div>
              <div><div className="text-3xl font-bold mb-2">2000+</div><div className="text-sm opacity-80">Beds</div></div>
              <div><div className="text-3xl font-bold mb-2">24/7</div><div className="text-sm opacity-80">Emergency Care</div></div>
            </div>
          </div>
        </div>
      </section>
      )}

      <Footer />
    </div>
  );
};

export default OurHospitals;